import React, { FC } from "react";
import type { LazyRoute } from "@tanstack/react-router";
import { RouterProvider } from "@tanstack/react-router";
import { AppLayoutModalProvider } from "shared-ts-ui-app-layout";
import type { RouteById, RouterIds } from "firefly-my-chimney-users-ts-router";
import { router } from "firefly-my-chimney-users-ts-router";
import { RootComponent } from "./RootComponent";
import { setMuiLicenseKey } from "shared-ts-mui";

setMuiLicenseKey();

// Generic to enforce that the route returned matches the route path
type LazyRouteFn<TRoutePath extends RouterIds> = () => Promise<
  LazyRoute<RouteById<(typeof router)["routeTree"], TRoutePath>>
>;

type RouterMap = {
  // __root__ is a special route that isn't lazy loaded, so we need to manually bind it
  // You could consider adding null to the returned type to have routes without rendering
  [K in Exclude<RouterIds, "__root__">]: LazyRouteFn<K>;
};

const routerMap: RouterMap = {
  "/": () =>
    import("firefly-my-chimney-users-ts-pages").then((d) => d.UsersPageRoute),
  "/users": () =>
    import("firefly-my-chimney-users-ts-pages").then(
      (d) => d.UsersPageRoute
    ) as any,
  "/addresses": () =>
    import("firefly-my-chimney-users-ts-pages").then(
      (d) => d.AddressesPageRoute
    ) as any,
  "/about": () =>
    import("firefly-my-chimney-users-ts-pages").then(
      (d) => d.AboutPageRoute
    ) as any,
  "/user": () =>
    import("firefly-my-chimney-users-ts-pages").then(
      (d) => d.UserPageRoute
    ) as any,
};

// Given __root__ is a special route that isn't lazy loaded, we need to update it manually
router.routesById["__root__"].update({
  component: RootComponent,
});

Object.entries(routerMap).forEach(([path, component]) => {
  const foundRoute = router.routesById[path as RouterIds];
  // Bind the lazy route to the actual route
  foundRoute.lazy(component as any);
});

export const App: FC = () => {
  return (
    <AppLayoutModalProvider>
      <RouterProvider router={router} />
    </AppLayoutModalProvider>
  );
};
